import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom";
import { animate, motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Toaster, toast } from "sonner";
import { ArrowRight, ChevronRight, Globe, HeartHandshake, ShieldCheck, Sparkles } from "lucide-react";
import DonationPortal from "@/components/DonationPortal";
import { galaContent, navigation } from "@/data/galaContent";

/* ── Constants ── */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_GOAL = 12_000_000;
const HERO_GLOW_INITIAL = { x: 44, y: 38 };
const HERO_PARALLAX_RANGE = 110;
const POLL_MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;
const VIEWPORT_ONCE = { once: true, amount: 0.2 };

const PILLAR_ICONS = [
  <HeartHandshake className="h-5 w-5 text-[#C5A059]" />,
  <Sparkles className="h-5 w-5 text-[#C5A059]" />,
  <ShieldCheck className="h-5 w-5 text-[#C5A059]" />,
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount || 0);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut", delay },
  }),
};

/* ── Shared Components ── */
const AnimatedValue = ({ value, prefix = "", suffix = "", testId }) => {
  const reduceMotion = useReducedMotion();
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (reduceMotion) {
      setDisplay(value);
      return undefined;
    }

    const controls = animate(0, value, {
      duration: 1.4,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(v),
    });

    return () => controls.stop();
    // animate is a stable import; value + reduceMotion are the real triggers
  }, [reduceMotion, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatted = useMemo(() => {
    return `${prefix}${Math.round(display).toLocaleString("en-US")}${suffix}`;
  }, [display, prefix, suffix]);

  return <span data-testid={testId}>{formatted}</span>;
};

const SectionHeading = ({ kicker, title, description, testId }) => (
  <div className="space-y-4">
    <p className="luxury-kicker" data-testid={`${testId}-kicker`}>
      {kicker}
    </p>
    <h2 className="font-display text-4xl tracking-tight text-white sm:text-5xl" data-testid={`${testId}-title`}>
      {title}
    </h2>
    {description ? (
      <p className="max-w-3xl text-sm text-zinc-300 sm:text-base" data-testid={`${testId}-description`}>
        {description}
      </p>
    ) : null}
  </div>
);

const GalaHeader = ({ showDonate = true }) => (
  <header className="sticky top-0 z-50 mx-auto max-w-7xl px-4 pt-4 sm:px-8">
    <div className="glass-panel flex flex-wrap items-center justify-between gap-4 px-5 py-4 sm:px-6">
      <div>
        <p className="luxury-kicker" data-testid="site-edition-label">{galaContent.event.edition}</p>
        <p className="font-display text-xl text-white" data-testid="site-title">
          {galaContent.event.title}
        </p>
      </div>
      {showDonate ? (
        <>
          <nav className="flex flex-wrap items-center gap-4 text-sm text-zinc-300">
            {navigation.map((item) => (
              <a
                key={item.id}
                href={item.id === "donation-portal" ? "/donate" : `#${item.id}`}
                className="transition hover:text-[#C5A059]"
                data-testid={`nav-link-${item.id}`}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <a href="/donate" className="luxury-button" data-testid="header-donate-button">
            Donate now
          </a>
        </>
      ) : null}
    </div>
  </header>
);

/* ── Data Hook ── */
const useDonationData = () => {
  const [tiers, setTiers] = useState([]);
  const [metrics, setMetrics] = useState(null);

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await fetch(`${API}/gala/metrics`);
      const data = await response.json();
      if (response.ok) setMetrics(data);
    } catch (_) { /* metrics refresh is non-critical */ }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [tiersResponse, metricsResponse] = await Promise.all([
          fetch(`${API}/gala/tiers`),
          fetch(`${API}/gala/metrics`),
        ]);
        const [tiersData, metricsData] = await Promise.all([
          tiersResponse.json(),
          metricsResponse.json(),
        ]);
        if (tiersResponse.ok) setTiers(tiersData);
        if (metricsResponse.ok) setMetrics(metricsData);
      } catch (_) { /* initial load failure is silent */ }
    };

    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { tiers, metrics, fetchMetrics };
};

/* ── Page Sections ── */
const HeroSection = () => {
  const reduceMotion = useReducedMotion();
  const [heroGlow, setHeroGlow] = useState(HERO_GLOW_INITIAL);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, reduceMotion ? 0 : HERO_PARALLAX_RANGE]);

  const handleHeroMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;
    setHeroGlow({ x, y });
  };

  return (
    <section className="relative overflow-hidden px-4 pb-10 pt-6 sm:px-8 lg:px-10" onMouseMove={handleHeroMove}>
      <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[36px] border border-white/10">
        <motion.img
          src={galaContent.imagery.hero}
          alt="Abstract red and gold editorial texture for gala hero"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ y: heroY }}
          data-testid="hero-background-image"
        />
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at ${heroGlow.x}% ${heroGlow.y}%, rgba(197,160,89,0.18), transparent 24%), linear-gradient(120deg, rgba(5,5,5,0.96) 10%, rgba(5,5,5,0.7) 52%, rgba(5,5,5,0.92) 100%)`,
          }}
        />
        <div className="relative z-10 grid gap-10 px-6 py-14 sm:px-10 lg:grid-cols-[1.1fr_0.9fr] lg:px-14 lg:py-20">
          <motion.div initial="hidden" animate="visible" className="space-y-8">
            <motion.div custom={0} variants={fadeUp}>
              <p className="luxury-kicker" data-testid="hero-edition-kicker">
                {galaContent.event.edition}
              </p>
            </motion.div>
            <motion.div custom={0.1} variants={fadeUp} className="space-y-5">
              <h1 className="font-display text-5xl leading-none tracking-tight text-white sm:text-6xl lg:text-[5rem]" data-testid="hero-headline">
                Red-carpet influence meets humanity.
              </h1>
              <p className="max-w-2xl text-base leading-relaxed text-zinc-200 sm:text-lg" data-testid="hero-subtitle">
                {galaContent.event.subtitle}
              </p>
            </motion.div>
            <motion.div custom={0.2} variants={fadeUp} className="flex flex-wrap gap-4">
              <a href="/donate" className="luxury-button" data-testid="hero-donate-button">
                Pledge <ArrowRight className="ml-2 h-4 w-4" />
              </a>
              <a href="#crisis-desk" className="luxury-button ghost-luxury" data-testid="hero-briefing-button">
                Review crisis briefing
              </a>
            </motion.div>
            <motion.div custom={0.3} variants={fadeUp} className="grid gap-4 sm:grid-cols-3">
              {galaContent.heroStats.map((item) => (
                <div key={item.label} className="glass-panel p-4" data-testid={`hero-stat-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">{item.label}</p>
                  <p className="mt-2 font-display text-2xl text-white">{item.value}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div initial="hidden" animate="visible" className="space-y-5 lg:pt-24">
            <motion.div custom={0.35} variants={fadeUp} className="grid gap-4 sm:grid-cols-2">
              <div className="glass-panel p-5" data-testid="event-date-card">
                <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">Date</p>
                <p className="mt-2 font-display text-2xl text-white">{galaContent.event.date}</p>
              </div>
              <div className="glass-panel p-5" data-testid="event-venue-card">
                <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">Venue</p>
                <p className="mt-2 text-sm text-zinc-200">{galaContent.event.venue}</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const CrisisDeskSection = () => (
  <section id="crisis-desk" className="section-shell">
    <SectionHeading
      kicker="About the crisis"
      title="A donor briefing shaped by current reporting and humanitarian reality."
      description="This section favours sober, source-backed summaries over spectacle. It is designed to help donors understand why emergency systems, lives, and family stability are at such risk when conflict and supply disruptions collide."
      testId="crisis-section-heading"
    />

    <div className="mt-10 grid gap-6 lg:grid-cols-1 lg:max-w-3xl">
      {galaContent.newsBriefs.map((brief, index) => (
        <motion.article
          key={brief.title}
          className="glass-panel p-6"
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT_ONCE}
          custom={index * 0.08}
          variants={fadeUp}
          data-testid={`news-brief-card-${index}`}
        >
          <h3 className="font-display text-3xl text-white" data-testid={`news-brief-title-${index}`}>
            {brief.title}
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-zinc-300" data-testid={`news-brief-summary-${index}`}>
            {brief.summary}
          </p>
          <a
            href={brief.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 text-sm text-[#C5A059] transition hover:gap-3"
            data-testid={`news-brief-source-link-${index}`}
          >
            Read source <ChevronRight className="h-4 w-4" />
          </a>
        </motion.article>
      ))}
    </div>
  </section>
);

const MissionSection = () => (
  <section id="mission" className="section-shell">
    <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
      <div className="glass-panel overflow-hidden" data-testid="mission-image-panel">
        <img
          src={galaContent.imagery.mission}
          alt="Respectful documentary-style medical relief imagery"
          className="aspect-[4/5] w-full object-cover object-center"
          data-testid="mission-image"
        />
      </div>

      <div className="space-y-6">
        <SectionHeading
          kicker="The mission"
          title="Emergency aid with women, girls, and families kept at the center."
          testId="mission-section-heading"
        />
        <div className="grid gap-4 sm:grid-cols-3">
          {galaContent.missionPillars.map((pillar, index) => (
            <div key={pillar.title} className="glass-panel p-5" data-testid={`mission-pillar-${index}`}>
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full border border-[#C5A059]/30 bg-[#C5A059]/10">
                {PILLAR_ICONS[index]}
              </div>
              <h3 className="font-display text-2xl text-white" data-testid={`mission-pillar-title-${index}`}>{pillar.title}</h3>
              <p className="mt-3 text-sm text-zinc-300" data-testid={`mission-pillar-text-${index}`}>{pillar.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const GuestsSection = () => (
  <section id="guests" className="section-shell">
    <SectionHeading
      kicker="Hosts & special guests"
      title="An editorial guest list aligned with visibility, credibility, and humanity-first purpose."
      testId="guest-section-heading"
    />
    <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
      {galaContent.guestProfiles.map((guest, index) => (
        <motion.article
          key={guest.name}
          className="overflow-hidden rounded-[28px] border border-white/6 bg-black/45 backdrop-blur-2xl"
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT_ONCE}
          custom={index * 0.06}
          variants={fadeUp}
          data-testid={`guest-profile-card-${index}`}
        >
          <div className="relative">
            <img
              src={guest.image}
              alt={`Editorial placeholder portrait for ${guest.name}`}
              className="aspect-[4/4.4] w-full object-cover object-center"
              style={guest.imagePosition ? { objectPosition: guest.imagePosition } : undefined}
              data-testid={`guest-profile-image-${index}`}
            />
          </div>
          <div className="p-6">
            <p className="text-xs uppercase tracking-[0.24em] text-[#C5A059]" data-testid={`guest-profile-role-${index}`}>{guest.role}</p>
            <h3 className="mt-3 font-display text-3xl text-white" data-testid={`guest-profile-name-${index}`}>{guest.name}</h3>
            <p className="mt-4 text-sm leading-relaxed text-zinc-300" data-testid={`guest-profile-bio-${index}`}>{guest.bio}</p>
          </div>
        </motion.article>
      ))}
    </div>
  </section>
);

const HistorySection = () => (
  <section id="history" className="section-shell">
    <div className="grid gap-6 lg:grid-cols-[1.02fr_0.98fr]">
      <div className="glass-panel overflow-hidden" data-testid="history-image-panel">
        <img
          src={galaContent.imagery.gala}
          alt="Elegant black-tie gala environment"
          className="aspect-[5/4] w-full object-cover object-center"
          data-testid="history-image"
        />
      </div>
      <div className="space-y-6">
        <SectionHeading
          kicker="Event history & partners"
          title="Edition II is grounded in sequel logic: prestige with proof, not prestige alone."
          description="The second edition echoes the inaugural gala's emphasis on measurable relief ( as was in Ukraine), then extends it with a sharper donor qualification model, stronger partner alignment, and a richer editorial briefing on the present crisis."
          testId="history-section-heading"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          {galaContent.inauguralImpact.map((metric, index) => (
            <div key={metric.label} className="glass-panel p-5" data-testid={`inaugural-impact-card-${index}`}>
              <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">{metric.label}</p>
              <p className="mt-2 font-display text-3xl text-white" data-testid={`inaugural-impact-value-${index}`}>{metric.value}</p>
            </div>
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {galaContent.partners.map((partner, index) => (
            <div key={partner.name} className="glass-panel p-5" data-testid={`partner-card-${index}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#C5A059]/30 bg-[#C5A059]/10">
                  <Globe className="h-5 w-5 text-[#C5A059]" />
                </div>
                <h3 className="font-display text-2xl text-white" data-testid={`partner-name-${index}`}>{partner.name}</h3>
              </div>
              <p className="mt-4 text-sm text-zinc-300" data-testid={`partner-description-${index}`}>{partner.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ── Page Components ── */
const GalaLandingPage = () => (
  <div className="App text-white">
    <GalaHeader />
    <main>
      <HeroSection />
      <CrisisDeskSection />
      <MissionSection />
      <GuestsSection />
      <HistorySection />
    </main>
  </div>
);

const FundraisingThermometer = ({ metrics }) => {
  const progressInitial = useMemo(() => ({ width: 0 }), []);
  const progressAnimate = useMemo(() => ({ width: `${metrics?.progress_percent || 0}%` }), [metrics?.progress_percent]);
  const progressTransition = useMemo(() => ({ duration: 1.2, ease: "easeOut" }), []);

  return (
    <div className="glass-panel p-6 sm:p-8" data-testid="donate-page-thermometer-panel">
      <p className="luxury-kicker" data-testid="donate-page-progress-kicker">Live fundraising thermometer</p>
      <div className="mt-5 flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">Committed now</p>
          <p className="font-display text-4xl text-white" data-testid="donate-page-total-committed">
            <AnimatedValue value={metrics?.total_committed_amount || 0} prefix="$" />
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">Goal</p>
          <p className="font-display text-2xl text-white" data-testid="donate-page-goal-amount">
            {formatCurrency(metrics?.goal_amount || DEFAULT_GOAL)}
          </p>
        </div>
      </div>
      <div className="mt-5 progress-track" data-testid="donate-page-progress-track">
        <motion.div
          className="progress-fill"
          initial={progressInitial}
          animate={progressAnimate}
          transition={progressTransition}
          data-testid="donate-page-progress-fill"
        />
      </div>
      <div className="mt-3 flex items-center justify-between text-sm text-zinc-300">
        <span data-testid="donate-page-progress-percent">{metrics?.progress_percent || 0}% of target</span>
        <span data-testid="donate-page-donor-count">{metrics?.donor_count || 0} confirmed donors</span>
      </div>
    </div>
  );
};

const DonatePage = () => {
  const { tiers, metrics, fetchMetrics } = useDonationData();

  return (
    <div className="App text-white">
      <header className="sticky top-0 z-50 mx-auto max-w-7xl px-4 pt-4 sm:px-8">
        <div className="glass-panel flex flex-wrap items-center justify-between gap-4 px-5 py-4 sm:px-6">
          <div>
            <p className="luxury-kicker" data-testid="donate-page-edition-label">{galaContent.event.edition}</p>
            <p className="font-display text-xl text-white" data-testid="donate-page-title">
              {galaContent.event.title}
            </p>
          </div>
          <a href="/" className="luxury-button ghost-luxury" data-testid="donate-page-return-home-button">
            Return to gala
          </a>
        </div>
      </header>

      <main>
        <section className="section-shell pb-6">
          <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="glass-panel p-6 sm:p-8" data-testid="donate-page-intro-panel">
              <p className="luxury-kicker" data-testid="donate-page-intro-kicker">Secure allocation</p>
              <h1 className="font-display text-4xl tracking-tight text-white sm:text-5xl" data-testid="donate-page-heading">
                Private donor giving is available here only.
              </h1>
              <p className="mt-4 max-w-3xl text-sm text-zinc-300 sm:text-base" data-testid="donate-page-description">
                This dedicated giving page keeps concierge commitments available when you choose to enter the donation flow.
              </p>
            </div>

            <FundraisingThermometer metrics={metrics} />
          </div>
        </section>

        <DonationPortal metrics={metrics} tiers={tiers} onRefreshMetrics={fetchMetrics} />
      </main>
    </div>
  );
};

const DonationSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({
    label: "Checking secure transfer\u2026",
    payment_status: "pending",
  });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;

    const pollStatus = async () => {
      attemptsRef.current += 1;
      try {
        const response = await fetch(`${API}/payments/checkout/status/${sessionId}`);
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || "Unable to confirm payment status.");
        }

        if (data.payment_status === "paid") {
          setStatus({ label: "Commitment confirmed. Thank you for giving at Edition II.", payment_status: "paid" });
          toast.success("Donation confirmed.");
          return;
        }

        if (data.status === "expired") {
          setStatus({ label: "This secure session expired before completion.", payment_status: "expired" });
          return;
        }

        setStatus({ label: "Payment is still processing securely\u2026", payment_status: data.payment_status || "pending" });
        if (attemptsRef.current < POLL_MAX_ATTEMPTS) {
          window.setTimeout(pollStatus, POLL_INTERVAL_MS);
        }
      } catch (err) {
        setStatus({ label: err.message, payment_status: "error" });
      }
    };

    pollStatus();
  }, [sessionId]);

  return (
    <div className="section-shell min-h-screen">
      <div className="mx-auto max-w-3xl glass-panel p-8 sm:p-12" data-testid="donation-success-page">
        <p className="luxury-kicker" data-testid="donation-success-kicker">Secure return</p>
        <h1 className="mt-4 font-display text-5xl text-white" data-testid="donation-success-heading">
          Donation status
        </h1>
        <p className="mt-6 text-lg text-zinc-300" data-testid="donation-success-status-message">
          {status.label}
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="glass-panel p-5" data-testid="donation-success-session-card">
            <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">Session ID</p>
            <p className="mt-2 break-all text-sm text-white">{sessionId || "Unavailable"}</p>
          </div>
          <div className="glass-panel p-5" data-testid="donation-success-payment-state-card">
            <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">Payment state</p>
            <p className="mt-2 font-display text-3xl text-white">{status.payment_status}</p>
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-4">
          <a href="/" className="luxury-button" data-testid="donation-success-return-home-button">
            Return to gala
          </a>
          <a href="/donate" className="luxury-button ghost-luxury" data-testid="donation-success-return-donate-button">
            Review giving tiers
          </a>
        </div>
      </div>
    </div>
  );
};

/* ── App Root ── */
const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<GalaLandingPage />} />
    <Route path="/donate" element={<DonatePage />} />
    <Route path="/donation/success" element={<DonationSuccessPage />} />
  </Routes>
);

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
      <Toaster position="top-right" richColors theme="dark" />
    </BrowserRouter>
  );
}

export default App;
